import React from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlignLeft, AlignCenter, AlignRight, Sparkles, Check, Trash2, Type } from 'lucide-react';
import { approveArtifactImage, rejectArtifactImage } from './api';

const WIDTHS = [['S', 240], ['M', 420], ['L', 640], ['Full', null]];

// Selection controls for an inline image: the Word-like sizing and alignment,
// plus the governed actions (refine, approve, remove).
export default function ImageBubbleMenu({ editor, canApprove, onRefine, onEditCaption, onChanged }) {
  const attrs = editor?.getAttributes('image') || {};
  const imageId = attrs['data-training-image-id'];
  const status = attrs['data-status'];

  const approve = useMutation({
    mutationFn: () => approveArtifactImage(imageId),
    onSuccess: () => {
      editor.chain().focus().updateAttributes('image', { 'data-status': 'approved' }).run();
      toast.success('Approved. Save the artifact to keep it.');
      onChanged?.();
    },
    onError: (e) => toast.error(e?.message || 'Not permitted to approve'),
  });

  const remove = useMutation({
    mutationFn: () => rejectArtifactImage(imageId),
    onSuccess: () => {
      editor.chain().focus().deleteSelection().run();
      toast.success('Image removed');
      onChanged?.();
    },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  if (!editor) return null;

  const btn = 'px-1.5 py-1 rounded text-[#c7d0e0] hover:bg-[#12202f]';

  return (
    <BubbleMenu editor={editor} shouldShow={({ editor: e }) => e.isActive('image')}>
      <div className="flex items-center gap-0.5 rounded-lg border border-[#2b3a4d] bg-[#0f1620] px-1 py-0.5 shadow-lg">
        {WIDTHS.map(([label, px]) => (
          <button
            key={label}
            className={`${btn} text-[10px]`}
            title={`Width ${label}`}
            onClick={() => editor.chain().focus().updateAttributes('image', { width: px }).run()}
          >
            {label}
          </button>
        ))}
        <div className="w-px h-4 mx-0.5 bg-[#2b3a4d]" />
        <button
          className={btn}
          title="Align left"
          onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()}
        >
          <AlignLeft size={13} />
        </button>
        <button
          className={btn}
          title="Align center"
          onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()}
        >
          <AlignCenter size={13} />
        </button>
        <button
          className={btn}
          title="Align right"
          onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()}
        >
          <AlignRight size={13} />
        </button>
        <div className="w-px h-4 mx-0.5 bg-[#2b3a4d]" />
        <button
          className={btn}
          title="Refine with JPRO Intelligence"
          onClick={() => onRefine?.({ imageId })}
        >
          <Sparkles size={13} />
        </button>
        {onEditCaption && (
          <button
            className={btn}
            title="Edit caption and alt text"
            onClick={() => onEditCaption({ imageId })}
          >
            <Type size={13} />
          </button>
        )}
        {canApprove && status === 'draft' && (
          <button
            className={`${btn} text-blue-400`}
            title="Approve this image"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
          >
            <Check size={14} />
          </button>
        )}
        <button
          className={`${btn} text-[#f7a23b]`}
          title="Remove this image"
          onClick={() => {
            if (window.confirm('Remove this image? This cannot be undone.')) remove.mutate();
          }}
          disabled={remove.isPending}
        >
          <Trash2 size={13} />
        </button>
        {status === 'draft' && (
          <span className="ml-1 mr-1 text-[10px] text-[#f7a23b]">Pending approval</span>
        )}
      </div>
    </BubbleMenu>
  );
}
